import { useMemo } from 'react'
import { View } from 'react-native'
import { useAuth } from '../lib/AuthContext'
import { useThemeColors } from '../lib/useThemeColors'
import { resolvePerson } from '../lib/personAvatar'
import { useSpecialJersey } from '../lib/useSpecialJerseys'
import PersonAvatar from './PersonAvatar'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { lighten, SPECIAL_BADGES } from './personAvatarConfig'
import { useShowAvatars } from '../lib/avatarPref'

/**
 * PersonAvatarFace — volto tondo dell'avatar persona per classifiche, liste e podio.
 * Ritaglia la testa con i capelli (e un accenno di colletto) dentro un cerchio. Sotto i 140 px
 * PersonAvatar usa gia i livelli piccoli.
 *
 * Props:
 * - userId: id dell'utente (serve per il default generato dall'id)
 * - settings: avatar_settings dell'utente (se manca, default dall'id; per me si usa il mio profilo)
 * - size: diametro del cerchio in px
 * - ring: colore dell'anello attorno alla faccia (oro, argento, bronzo per i primi tre)
 */
// Fondo: tinta pastello del colore della competizione (stesso colore chiaro in tema chiaro e scuro: i contorni
// scuri dell'avatar e i capelli neri restano leggibili su qualsiasi pelle)
const FACE_BG_LIGHTEN = 0.8
const CROP = 0.66          // parte dell'immagine mostrata
const X0 = 0.17            // inizio del ritaglio a sinistra (frazione della larghezza)
const Y0 = 0.0             // inizio in alto

export default function PersonAvatarFace({ userId, settings, size = 36, ring = null }) {
  const tc = useThemeColors()
  const { user, avatarSettings } = useAuth()
  const isMe = !!user?.id && userId === user.id
  const src = isMe ? avatarSettings : settings
  const special = useSpecialJersey(userId)
  const show = useShowAvatars()   // interruttore del Profilo: spento = solo il nome, niente avatar

  const person = useMemo(() => {
    const p = resolvePerson(src, userId)
    if (!src?.person?.main) { p.main = tc.rosa; p.accent = tc.rosa } // maglia nel colore della competizione
    return p
  }, [src, userId, tc.rosa])

  if (!show) return null
  const full = size / CROP
  // Badge iridato (campione del mondo nella lega): il volto ritaglia la maglia, quindi la fascia non si vede.
  // Disco a 5 bande con bordo bianco, sull'angolo in basso a destra, un terzo del volto (minimo 10 px).
  const spec = SPECIAL_BADGES[special]
  const badge = spec ? Math.max(10, Math.round(size * 0.36)) : 0
  const badgeBorder = size >= 40 ? 2 : 1.5
  // L'anello e' un livello sopra (non un bordo del contenitore): un bordo restringe l'area interna e sposta
  // il volto di mezzo spessore verso destra e in basso, quindi non sarebbe centrato nel cerchio.
  return (
    <View style={{ width: size, height: size }}>
      <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden', backgroundColor: lighten(tc.rosa, FACE_BG_LIGHTEN) }}>
        <View style={{ marginLeft: -full * X0, marginTop: -full * Y0 }}>
          <PersonAvatar person={person} size={full} special={special} />
        </View>
        {ring && (
          <View
            pointerEvents="none"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: size / 2, borderWidth: 2.5, borderColor: ring }}
          />
        )}
      </View>
      {spec && (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute', right: -badge * 0.22, bottom: -badge * 0.15, width: badge, height: badge,
            borderRadius: badge / 2, borderWidth: badgeBorder, borderColor: '#FFFFFF', overflow: 'hidden', backgroundColor: '#FFFFFF',
            flexDirection: spec.vertical ? 'row' : 'column',
          }}
        >
          {spec.colors.map(c => <View key={c} style={{ flex: 1, backgroundColor: c }} />)}
          {spec.star && (
            <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}>
              <MaterialCommunityIcons name="star" size={Math.round(badge * 0.62)} color={spec.star} />
            </View>
          )}
        </View>
      )}
    </View>
  )
}
